import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import useUserStore from '../store/userStore';
import authService from '../services/authService';

/**
 * Improved CopySVGButton component that works with the new authentication system
 */
const CopySVGButton = ({
  assets,
  is_purchased,
  isSubscriptionActive,
  onBeforeAction,
  onActionComplete,
}) => {
  // States
  const [isDownloading, setIsDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [svgData, setSvgData] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Access user data from store
  const isLoggedIn = useUserStore((state) => state.isLoggedIn);
  const user = useUserStore((state) => state.user);
  const checkAccessWithProfileRefresh = useUserStore((state) => state.checkAccessWithProfileRefresh);

  /**
   * Checks if user has access to the SVG
   */
  const hasAccessToSVG = useCallback(() => {
    // Basic icons are always free
    const isFreeAsset = assets.every(asset => 
      asset.is_free === true || asset.collection === 'basic'
    );
    
    // For premium SVGs, user needs to have purchased it or have an active subscription
    return isFreeAsset || is_purchased || isSubscriptionActive;
  }, [assets, is_purchased, isSubscriptionActive]);

  /**
   * Fetches SVG data from the server with improved error handling
   */
  const fetchSVGData = useCallback(async () => {
    if (!assets || assets.length === 0) {
      throw new Error('No SVG assets available.');
    }

    // Validate assets
    const missingAssets = assets.filter(
      (asset) => !asset.slug || !asset.collection || !asset.format
    );

    if (missingAssets.length > 0) {
      throw new Error('Some assets are missing required parameters.');
    }

    // Check if we need authentication for these assets
    const allFreeOrBasic = assets.every(asset => 
      asset.is_free === true || asset.collection === 'basic'
    );
    
    // If auth is needed but user isn't logged in, fail early
    if (!allFreeOrBasic && !isLoggedIn) {
      throw new Error('Authentication required to fetch this SVG');
    }

    // Format request payload
    const requestData = {
      assets: assets.map((asset) => ({
        slug: asset.slug,
        collection: asset.collection,
        media_type: asset.media_type || 'vector',
        is_free: asset.is_free !== undefined ? asset.is_free : false,
        format: asset.format || ['svg'],
      })),
    };

    // Вывод информации для отладки
    console.log('SVG Request:', {
      allFreeOrBasic,
      isLoggedIn,
      isPurchased: is_purchased,
      hasSubscription: isSubscriptionActive,
      assets: requestData.assets,
    });

    try {
      let response;

      // For free/basic assets or when the user isn't logged in
      if (allFreeOrBasic) {
        console.log('Fetching free/basic SVG without authentication');
        response = await fetch('https://davoodacom-download.willsavetheworld.workers.dev/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      } else {
        // Для премиум ассетов используем authService для получения JWT токена
        console.log('Fetching premium SVG with authentication');
        const jwtToken = await authService.getValidToken();
        
        if (!jwtToken) {
          throw new Error('Authentication required but no valid JWT token available');
        }
        
        console.log('Got JWT token, length:', jwtToken ? jwtToken.length : 'null');
        
        // Проверка доступа на сервере перед попыткой загрузки
        const accessCheckResponse = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/download_access_check`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwtToken}`
          },
          body: JSON.stringify(requestData)
        });
        
        if (!accessCheckResponse.ok) {
          const errorDetail = await accessCheckResponse.json();
          console.error('Access check failed:', errorDetail);
          throw new Error(errorDetail.reason || 'Access denied. Please make sure you have purchased this asset or have an active subscription.');
        }
        
        // Выполняем запрос с добавлением JWT токена
        response = await fetch('https://davoodacom-download.willsavetheworld.workers.dev/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwtToken}`
          },
          body: JSON.stringify(requestData)
        });
      }

      // Обработка ответа
      if (!response.ok) {
        // Обработка ошибок с более понятными сообщениями
        let errorMessage = 'Server error occurred.';
        
        try {
          const errorText = await response.text();
          
          // Если получен ответ в JSON формате, пытаемся его распарсить
          if (errorText.startsWith('{') && errorText.endsWith('}')) {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.detail || errorJson.message || errorText;
          } else {
            errorMessage = errorText;
          }
        } catch (e) {
          errorMessage = `Server error: ${response.status}`;
        }
        
        // Более понятные сообщения для конкретных кодов ошибок
        if (response.status === 401 || response.status === 403) {
          errorMessage = "You don't have permission to download this asset. Please check if you've purchased it or have an active subscription.";
        }
        
        throw new Error(errorMessage);
      }

      // Парсинг ответа
      const responseData = await response.json();
      if (!responseData || !responseData.images) {
        throw new Error('Invalid response from server.');
      }

      return responseData.images;
    } catch (error) {
      console.error('Failed to fetch SVG data:', error);
      throw error;
    }
  }, [assets, isLoggedIn, is_purchased, isSubscriptionActive]);

  /**
   * Preloads SVG data if user has access
   */
  const preloadSVG = useCallback(async () => {
    if (hasFetched || !hasAccessToSVG()) return;
    
    // Skip preload if not logged in for non-free assets
    const needsAuth = assets.some(asset => asset.collection !== 'basic' && !asset.is_free);
    if (needsAuth && !isLoggedIn) {
      console.log('Skipping SVG preload - auth required but user not logged in');
      return;
    }
    
    try {
      setIsDownloading(true);
      const images = await fetchSVGData();
      
      if (images && images.length === 1) {
        const { formats } = images[0];
        if (formats && formats.svg) {
          // Store SVG data for later use
          setSvgData(formats.svg);
        }
      }
      setHasFetched(true);
    } catch (error) {
      console.error('Failed to preload SVG:', error);
      // Don't show error to user during preload
    } finally {
      setIsDownloading(false);
    }
  }, [fetchSVGData, hasFetched, hasAccessToSVG, assets, isLoggedIn]);

  /**
   * Copies SVG to clipboard
   */
  const copySVG = useCallback(async (svgString) => {
    try {
      // Try to use modern clipboard API
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(svgString);
        return true;
      }
      
      // Fallback method
      const textarea = document.createElement('textarea');
      textarea.value = svgString;
      document.body.appendChild(textarea);
      textarea.select();
      const result = document.execCommand('copy');
      document.body.removeChild(textarea);
      return result;
    } catch (error) {
      console.error('Copy failed:', error);
      return false;
    }
  }, []);

  /**
   * Handles click on the copy button
   */
  const handleCopyClick = async (e) => {
    e.preventDefault();
    
    // Prevent actions if already downloading or showing success
    if (isDownloading || successMessage) return;
    
    // Очищаем предыдущие сообщения об ошибках
    setErrorMessage('');
    
    // Check access to SVG
    if (!hasAccessToSVG()) {
      // For non-basic, non-free assets, force refresh profile to check subscriptions
      if (assets.some(asset => asset.collection !== 'basic' && !asset.is_free)) {
        const assetId = assets[0]?.slug || '';
        const collection = assets[0]?.collection || '';
        
        setIsDownloading(true);
        try {
          // Check if user has access through subscription or purchase
          const hasAccess = await checkAccessWithProfileRefresh(assetId, collection);
          
          if (!hasAccess) {
            setErrorMessage('You must purchase this icon or have an active subscription to copy SVG.');
            return;
          }
        } catch (error) {
          setErrorMessage('Failed to verify access. Please try again.');
          return;
        } finally {
          setIsDownloading(false);
        }
      } else {
        setErrorMessage('You must purchase this icon to copy SVG.');
        return;
      }
    }
    
    // Execute onBeforeAction callback if provided
    if (onBeforeAction) {
      try {
        const canContinue = await Promise.resolve(onBeforeAction());
        if (!canContinue) return;
      } catch (error) {
        console.error('Error in onBeforeAction:', error);
        setErrorMessage('Failed to prepare for copy.');
        return;
      }
    }
    
    setIsDownloading(true);
    
    try {
      // Use preloaded data or fetch new
      let svgContent = svgData;
      
      if (!svgContent) {
        const images = await fetchSVGData();
        if (!images || images.length === 0) {
          throw new Error('No SVG content available.');
        }
        
        const { formats } = images[0];
        if (!formats || !formats.svg) {
          throw new Error('SVG format not available for this icon.');
        }
        
        svgContent = formats.svg;
        setSvgData(svgContent);
      }
      
      // Copy SVG to clipboard
      const success = await copySVG(svgContent);
      
      if (success) {
        setSuccessMessage('Copied!');
        setRetryCount(0); // Сбрасываем счетчик попыток при успехе
        
        if (onActionComplete) {
          onActionComplete();
        }
      } else {
        throw new Error('Failed to copy to clipboard.');
      }
    } catch (error) {
      console.error('SVG copy error:', error);
      
      // Увеличиваем счетчик попыток
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);
      
      // Сбрасываем кэшированные данные после нескольких ошибок
      if (newRetryCount >= 2) {
        setSvgData(null);
        setHasFetched(false);
      }
      
      // Выводим понятное сообщение об ошибке
      if (error.message.includes('403') || error.message.includes('permission') || 
          error.message.includes('access denied') || error.message.includes('unauthorized')) {
        setErrorMessage("Cannot access this SVG. Please check if you have purchased it or have an active subscription.");
      } else {
        setErrorMessage(error.message || 'Error copying SVG.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Preload SVG on component mount if user has access
  useEffect(() => {
    // Add a small delay to allow authentication to initialize
    const timer = setTimeout(() => {
      if (hasAccessToSVG()) {
        preloadSVG();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [hasAccessToSVG, preloadSVG, isLoggedIn, user]);

  // Auto-hide success message after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 1500); // Увеличиваем время показа сообщения
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Determine button text and class
  const displayText = successMessage || (isDownloading ? 'Loading...' : 'Copy SVG');
  const linkClass = successMessage 
    ? 'copy-link copy-link--success' 
    : (errorMessage ? 'copy-link copy-link--error' : 'copy-link');
  
  // Button is disabled for premium assets if user doesn't have access
  const hasNonFreeAsset = assets.some(asset => !asset.is_free && asset.collection !== 'basic');
  const isButtonDisabled = hasNonFreeAsset && !is_purchased && !isSubscriptionActive;

  // Use fixed width for the button to prevent size changes during state transitions
  const buttonStyle = {
    cursor: isDownloading ? 'wait' : (isButtonDisabled ? 'not-allowed' : 'pointer'),
    opacity: isDownloading ? 0.7 : (isButtonDisabled ? 0.3 : 1),
    minWidth: '80px',
    display: 'inline-block'
  };

  return (
    <div>
      {!isButtonDisabled && (
        <a
          href="#"
          className={linkClass}
          onClick={handleCopyClick}
          style={buttonStyle}
          disabled={isDownloading}
        >
          {displayText}
        </a>
      )}

      {isButtonDisabled && (
        <span
          className="copy-link copy-link--disabled"
          style={{ 
            opacity: 0.3, 
            cursor: 'not-allowed',
            minWidth: '80px',
            display: 'inline-block'
          }}
          title="Available after purchase"
        >
          {displayText}
        </span>
      )}

      {errorMessage && (
        <p style={{ color: 'red', marginTop: '5px', fontSize: '0.85rem' }}>{errorMessage}</p>
      )}
    </div>
  );
};

CopySVGButton.propTypes = {
  assets: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      collection: PropTypes.string.isRequired,
      media_type: PropTypes.string,
      is_free: PropTypes.bool,
      format: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  is_purchased: PropTypes.bool,
  isSubscriptionActive: PropTypes.bool,
  onBeforeAction: PropTypes.func,
  onActionComplete: PropTypes.func,
};

CopySVGButton.defaultProps = {
  is_purchased: false,
  isSubscriptionActive: false,
  onBeforeAction: null,
  onActionComplete: null,
};

export default CopySVGButton;